import React, { useEffect, useMemo, useState } from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { AnimatePresence, motion } from 'framer-motion';
import { DropdownMenu } from '../dropdown/Dropdown';
import { Button } from '../buttons/Button';

import {
	parseYearMonth,
	padMonth,
	createDefaultYears,
} from './year-month-picker-utils';

const monthLabels = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
] as const;

const yearMonthPickerClassNames = {
	trigger:
		'w-180 h-32 justify-between rounded-s bg-base-1 px-10 py-6 text-14 text-text-primary gap-8',
	content: 'w-272 max-w-[calc(100vw-32px)] p-0 overflow-visible',
	navButton:
		'h-32 w-32 inline-flex items-center justify-center rounded-xxs border border-transparent transition-colors',
	monthButton:
		'h-36 cursor-pointer rounded-xxs border border-transparent px-8 text-14 inline-flex items-center justify-center transition-colors',
};

type YearMonthPickerProps = {
	value: string;
	onValueChange: (value: string) => void;
	years?: number[];
	placeholder?: string;
	className?: string;
	triggerClassName?: string;
	disabled?: boolean;
	testid?: string;
	/** Rendered before the label, e.g. a calendar icon. */
	triggerIcon?: React.ReactNode;
	/** Overrides the default "Mmm YYYY" trigger text. */
	triggerLabel?: string;
};

export function YearMonthPicker({
	value,
	onValueChange,
	years,
	placeholder = 'Select month',
	className,
	triggerClassName,
	disabled,
	testid,
	triggerIcon,
	triggerLabel: triggerLabelOverride,
}: YearMonthPickerProps) {
	const parsedValue = parseYearMonth(value);
	const nowYear = new Date().getFullYear();
	const yearOptions = useMemo(
		() => (years && years.length > 0 ? years : createDefaultYears(nowYear)),
		[years, nowYear]
	);

	const [open, setOpen] = useState<boolean>(false);
	const [yearAnimationDirection, setYearAnimationDirection] = useState<1 | -1>(
		1
	);
	const [selectedYear, setSelectedYear] = useState<number>(
		parsedValue?.year ?? yearOptions[0] ?? nowYear
	);
	useEffect(() => {
		if (parsedValue?.year) {
			setSelectedYear(parsedValue.year);
			return;
		}
		setSelectedYear((previousYear) => {
			if (yearOptions.includes(previousYear)) {
				return previousYear;
			}
			return yearOptions[0] ?? nowYear;
		});
	}, [nowYear, parsedValue?.year, yearOptions]);

	const defaultTriggerLabel = parsedValue
		? `${monthLabels[parsedValue.month - 1]} ${parsedValue.year}`
		: placeholder;
	const triggerLabel = triggerLabelOverride ?? defaultTriggerLabel;
	const selectedYearIndex = yearOptions.findIndex(
		(year) => year === selectedYear
	);
	const canSelectPreviousYear = selectedYearIndex > 0;
	const canSelectNextYear =
		selectedYearIndex >= 0 && selectedYearIndex < yearOptions.length - 1;

	function handleSelectYear(nextYear: number | undefined) {
		if (!nextYear) return;
		setYearAnimationDirection(nextYear > selectedYear ? 1 : -1);
		setSelectedYear(nextYear);
	}

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenu.Trigger asChild>
				<Button
					variant="outline"
					size="xs"
					disabled={disabled}
					data-testid={testid}
					className={cn(yearMonthPickerClassNames.trigger, triggerClassName)}
				>
					<span className="flex items-center gap-8">
						{triggerIcon}
						{triggerLabel}
					</span>
					<Icon
						name="chevronDown"
						className={cn(
							'transition-transform',
							open ? 'rotate-180' : 'rotate-0'
						)}
					/>
				</Button>
			</DropdownMenu.Trigger>

			<DropdownMenu.Content
				align="start"
				forceMount
				className={cn(yearMonthPickerClassNames.content, className)}
			>
				<AnimatePresence>
					{open && (
						<motion.div
							key="year-month-picker-content"
							initial={{ opacity: 0, y: -6, scale: 0.98 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: -4, scale: 0.98 }}
							transition={{ duration: 0.18, ease: 'easeOut' }}
							className="p-12"
						>
							<div className="flex items-center justify-between pb-10">
								<button
									type="button"
									aria-label="Previous year"
									disabled={!canSelectPreviousYear}
									onClick={() => {
										if (!canSelectPreviousYear) return;
										handleSelectYear(yearOptions[selectedYearIndex - 1]);
									}}
									className={cn(
										yearMonthPickerClassNames.navButton,
										canSelectPreviousYear
											? 'cursor-pointer hover:border-interactive hover:bg-brand-blue-50'
											: 'opacity-40 cursor-not-allowed'
									)}
								>
									<Icon name="chevronLeft" />
								</button>

								<div
									className="w-72 overflow-hidden text-center"
									aria-live="polite"
									aria-atomic="true"
								>
									<AnimatePresence custom={yearAnimationDirection} mode="wait">
										<motion.div
											key={selectedYear}
											custom={yearAnimationDirection}
											initial={{ opacity: 0, x: yearAnimationDirection * 16 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: yearAnimationDirection * -16 }}
											transition={{ duration: 0.2, ease: 'easeOut' }}
											className="text-14 font-medium text-text-primary"
										>
											{selectedYear}
										</motion.div>
									</AnimatePresence>
								</div>

								<button
									type="button"
									aria-label="Next year"
									disabled={!canSelectNextYear}
									onClick={() => {
										if (!canSelectNextYear) return;
										handleSelectYear(yearOptions[selectedYearIndex + 1]);
									}}
									className={cn(
										yearMonthPickerClassNames.navButton,
										canSelectNextYear
											? 'cursor-pointer hover:border-interactive hover:bg-brand-blue-50'
											: 'opacity-40 cursor-not-allowed'
									)}
								>
									<Icon name="chevronRight" />
								</button>
							</div>

							<div className="overflow-hidden">
								<AnimatePresence custom={yearAnimationDirection} mode="wait">
									<motion.div
										key={`months-${selectedYear}`}
										custom={yearAnimationDirection}
										initial={{ opacity: 0, x: yearAnimationDirection * 24 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: yearAnimationDirection * -24 }}
										transition={{ duration: 0.2, ease: 'easeOut' }}
										className="grid grid-cols-3 gap-0 rounded-s"
									>
										{monthLabels.map((monthLabel, index) => {
											const month = index + 1;
											const isSelected =
												parsedValue?.year === selectedYear &&
												parsedValue.month === month;
											return (
												<button
													key={`${selectedYear}-${month}`}
													type="button"
													aria-label={`${monthLabel} ${selectedYear}`}
													aria-pressed={isSelected}
													onClick={() => {
														onValueChange(`${selectedYear}-${padMonth(month)}`);
														setOpen(false);
													}}
													className={cn(
														yearMonthPickerClassNames.monthButton,
														'hover:border-interactive',
														isSelected &&
															'border-interactive bg-interactive text-white font-medium'
													)}
												>
													{monthLabel}
												</button>
											);
										})}
									</motion.div>
								</AnimatePresence>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</DropdownMenu.Content>
		</DropdownMenu>
	);
}
